<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    disabled?: boolean
    invalidSerials?: string[]
    expectedTotalInverters?: number
  }>(),
  { invalidSerials: () => [] },
)

const serials = defineModel<string[]>({ default: () => [] })

const inputValue = ref('')

const invalidSerialsSet = computed(() => new Set(props.invalidSerials))

function serialHasError(serial: string) {
  return invalidSerialsSet.value.has(serial)
}

function addSerial(raw: string) {
  if (props.disabled) return
  const values = raw.split(',').map(s => s.trim()).filter(Boolean)
  const unique = values.filter(v => !serials.value.includes(v))
  if (unique.length)
    serials.value = [...serials.value, ...unique]
  inputValue.value = ''
}

function onKeydown(e: KeyboardEvent) {
  if (props.disabled) return
  if (e.key === 'Enter' || e.key === ',') {
    e.preventDefault()
    if (inputValue.value.trim())
      addSerial(inputValue.value)
  }
  else if (e.key === 'Backspace' && !inputValue.value && serials.value.length) {
    serials.value = serials.value.slice(0, -1)
  }
}

function onBlur() {
  if (props.disabled) return
  if (inputValue.value.trim())
    addSerial(inputValue.value)
}

function removeSerial(serial: string) {
  if (props.disabled) return
  serials.value = serials.value.filter(s => s !== serial)
}
</script>

<template>
  <div class="serial-input-field">
    <div
      class="serial-label-row"
      :class="{ 'serial-label-row--has-count': expectedTotalInverters != null }"
    >
      <span class="serial-label">Números de série</span>
      <div
        v-if="expectedTotalInverters != null"
        class="serial-count-container"
      >
        <span class="serial-count">
          {{ serials.length }} de {{ expectedTotalInverters }} inseridos
        </span>
      </div>
    </div>

    <div class="serial-input-wrapper" :class="{ 'serial-input-wrapper--disabled': disabled }">
      <span
        v-for="serial in serials"
        :key="serial"
        class="serial-tag"
        :class="{ 'serial-tag--error': serialHasError(serial) }"
      >
        <span class="serial-tag-label">{{ serial }}</span>
        <button
          type="button"
          class="serial-tag-remove"
          :aria-label="`Remover ${serial}`"
          :disabled="disabled"
          @click="removeSerial(serial)"
        >
          <svg
            class="serial-tag-remove-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </span>

      <input
        v-model="inputValue"
        class="serial-input"
        :placeholder="serials.length ? '' : 'Digite o(s) número(s)'"
        :disabled="disabled"
        @keydown="onKeydown"
        @blur="onBlur"
      >
    </div>

    <p class="serial-help">
      Digite o número e tecle ENTER ou vírgula se precisar incluir mais de um inversor.
    </p>
  </div>
</template>

<style scoped lang="scss">
.serial-input-field {
  @apply flex flex-col gap-2;

  > .serial-label-row {
    @apply relative flex items-center;

    &--has-count {
      @apply pr-[6.5rem];
    }

    > .serial-label {
      @apply fonts-body-small-bold;
      @apply text-neutral-low-dark;
    }

    > .serial-count-container {
      @apply absolute right-0 top-0;

      > .serial-count {
        @apply flex items-center justify-center gap-quark;
        @apply rounded-full bg-neutral-high-light;
        @apply text-neutral-low-medium text-micro font-regular;
        padding: 2px 4px;
        line-height: 115%;
      }
    }
  }

  > .serial-input-wrapper {
    @apply flex flex-wrap items-center;
    @apply gap-quark;
    @apply min-h-xs px-micro py-quark;
    @apply rounded-md border-a-xs border-neutral-high-medium;
    @apply bg-neutral-high-pure;
    @apply cursor-text;

    &--disabled {
      @apply cursor-default bg-neutral-high-light border-neutral-high-medium;

      > .serial-tag {
        @apply border-neutral-low-medium border-opacity-light;
      }

      > .serial-input {
        @apply text-neutral-low-medium;

        &::placeholder {
          @apply text-neutral-high-dark;
        }
      }
    }

    > .serial-tag {
      @apply flex items-center gap-1;
      @apply h-6;
      @apply px-nano;
      @apply border-neutral-low-medium border-opacity-medium border-[1px] rounded-sm;

      &--error {
        @apply border-feedback-negative-pure;

        > .serial-tag-label {
          @apply text-feedback-negative-pure;
        }
      }

      > .serial-tag-label {
        @apply min-w-0 truncate text-neutral-low-medium fonts-body-small-regular;
      }

      > .serial-tag-remove {
        @apply flex items-center justify-center flex-shrink-0;
        @apply bg-transparent border-0 p-0;
        @apply cursor-pointer;
        @apply w-4 h-4;
        @apply text-neutral-low-medium;

        &:disabled {
          @apply cursor-not-allowed;
          @apply opacity-50;
        }

        > .serial-tag-remove-icon {
          @apply block flex-shrink-0;
        }
      }
    }

    > .serial-input {
      @apply flex-1 min-w-[140px];
      @apply border-0 outline-none bg-transparent;
      @apply fonts-body-medium-regular text-neutral-low-medium;

      &::placeholder {
        @apply text-neutral-high-dark;
      }
    }
  }

  > .serial-help {
    @apply m-0;
    @apply fonts-body-small-regular;
    @apply text-neutral-low-light;
  }
}
</style>
